import { ChangeDetectorRef, Component, Injector, NgZone } from '@angular/core';
import { FieldArrayType, FormlyFieldConfig } from '@ngx-formly/core';
import { v4 as uuidv4 } from 'uuid';
import { RepeaterService } from '../../service/repeater-service/repeater.service';
import { Art57Section } from '../../ui/Art57Section/Art57Section';
import { BulkupdateEventsService } from '../../service/bulk-update-event-service/bulk-update-event.service';
import { operationActionEnum } from '../../common/enum/bulkExportEnum';
import { BulkService } from '../../service/bulk-service/bulk.service';
import { LoggerService } from '../../service/logger-service/logger.service';
import { take } from 'rxjs';

@Component({
  selector: 'app-bulk-update-repeater',
  templateUrl: './bulk-update-repeater.component.html',
  styleUrl: './bulk-update-repeater.component.scss',
})
export class BulkUpdateRepeaterComponent extends FieldArrayType {
  uuid = uuidv4();

  constructor(
    private injector: Injector,
    private cdr: ChangeDetectorRef,
    private ngZone: NgZone,
  ) {
    super();
    this.injector.get(RepeaterService).setRepeater(this.uuid, this);
  }

  ngOnInit() {
    this.field.props = { ...this.field.props, repeaterId: this.uuid };
    this.initializeRepeaterChildrenComponent();

    this.load('load');

    if (
      this.field.props['_initialized'] !== undefined &&
      this.field.props['_initialized'] == true
    ) {
      return;
    } else {
      this.field.props['_initialized'] = true;
      this.injector.get(BulkupdateEventsService).loadActions$.subscribe(() => {
        this.load('reload');
      });
    }
  }

  ngOnDestroy(): void {
    this.injector.get(RepeaterService).clearRepeater(this.uuid);
  }

  initializeRepeaterChildrenComponent() {
    const pendingGroup = this.field.fieldGroup?.find(
      (group: FormlyFieldConfig) => {
        const fields = group.fieldGroup ?? [];
        return fields.some(
          (f) =>
            f.key === 'componentID' && f.formControl?.value === 'pending_init',
        );
      },
    );

    if (pendingGroup) {
      const componentField = pendingGroup.fieldGroup!.find(
        (f) => f.key === 'componentID',
      );
      const newId = uuidv4();
      componentField?.formControl?.setValue(newId);
      pendingGroup.model['componentID'] = newId;
    }
  }

  getComponentId(): string {
    const componentGroup = this.field.fieldGroup?.find((group) => {
      // Busca el field con key === 'componentID' dentro del group
      const componentField = group.fieldGroup?.find(
        (f) => f.key === 'componentID',
      );

      // Encontrado? entonces este es el grupo que queremos
      return componentField !== undefined;
    });

    // Ahora obtenemos su defaultValue:
    const defaultValue = componentGroup?.fieldGroup?.find(
      (f) => f.key === 'componentID',
    )?.formControl?.value;

    return defaultValue;
  }

  repeaterLength(): number {
    const repeaterId = this.field.props!['repeaterId'];
    const repeater = this.injector.get(RepeaterService).getRepeater(repeaterId);
    return repeater.field.fieldGroup.length;
  }

  override add() {
    super.add();
    this.initializeRepeaterChildrenComponent();
  }

  private waitForFormlyBuild(): Promise<void> {
    return new Promise<void>((resolve) => {
      // 1) Espera a que Angular quede estable (terminen microtareas pendientes)
      this.ngZone.onStable.pipe(take(1)).subscribe(() => {
        // 2) Da 1 frame para montar vistas/hijos (hooks de componentes)
        requestAnimationFrame(() => resolve());
      });
    });
  }

  load(type: string) {
    const bulkUpdate = this.injector.get(Art57Section).bulkupdate;
    if (bulkUpdate.actionSetLoaded) {
      return;
    }

    const bulkSection = this.injector.get(Art57Section).bulkupdate;
    const bulkService = this.injector.get(BulkService);
    const item = bulkSection.selectedItem;
    bulkService.operationActions(item, operationActionEnum.reload).subscribe({
      next: async (data) => {
        if (data) {
          const bulkUpdate = this.injector.get(Art57Section).bulkupdate;
          bulkUpdate.isLoadingActionSet = true;
          bulkUpdate.criteriaModel.actionset = {
            action: [{ componentID: 'pending_init' }],
          };
          this.options.build?.();
          if (data.body) {
            data.body.action.forEach((e: any) => {
              e['componentID'] = uuidv4();
            });
          }
          bulkUpdate.criteriaModel.actionset = data.body;
          this.options.build?.();

          this.cdr.detectChanges();

          await this.waitForFormlyBuild();

          bulkUpdate.isLoadingActionSet = false;
          bulkUpdate.actionSetLoaded = true;
          this.injector
            .get(Art57Section)
            .showSuccessTooltipMessage(
              'Actions ' + type + ' successfully completed',
            );
        } else {
          this.injector
            .get(Art57Section)
            .showErrorTooltipMessage('Actions ' + type + ' failed');
        }
      },
      error: (err) => {
        this.injector
          .get(Art57Section)
          .showErrorTooltipMessage('Actions ' + type + ' failed');
        this.injector.get(LoggerService).err(err);
      },
    });
  }
}
